import { Star, StarBorder, MoreHoriz } from '@mui/icons-material';
// import CopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/LockOutlined';
import ShareIcon from '@mui/icons-material/Share';
import { Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Drawer } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, generatePath } from 'react-router-dom';

import { useDeleteCategoryMutation } from 'api/mutations/useDeleteCategoryMutation';
import { useToggleCategoryFavouriteMutation } from 'api/mutations/useToggleCategoryFavouriteMutation';
import { NewConfirmDialog } from 'components';
import { DocumentCategoryIcons } from 'constants/DocumentCategoryIcons';
import { useAuth, useModal, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { DocumentCategory } from 'types';
import { formatCreatedAt } from 'utils/dateHelpers';
import { isSystemCategory, isUntaggedCategory } from 'validations';

import { CreateDocumentCategoriesForm } from '../CreateDocumentCategoriesForm';
// import { DuplicateCategoryModal } from '../DuplicateCategoryModal';
import { ShareAccessModal } from '../ShareAccessModal';
import {
  CardW,
  IconBox,
  CardContent,
  TitleText,
  SubText,
  CardActions,
  CardLeft,
  Description,
  ListMeta,
  ListTitle,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetHeaderText,
} from './styled';

type DocumentCategoryItemTypes = {
  isExpanded: boolean;
  toggleExpand: () => void;
  fromSettings?: boolean;
  isManageable?: boolean;
  viewMode?: string;
  category?: DocumentCategory;
} & DocumentCategory;

export const DocumentCategoryItem = ({
  id: categoryId,
  label,
  icon,
  tag,
  description,
  created_at,
  is_favourite,
  viewMode = 'grid',
  category,
  isManageable,
}: DocumentCategoryItemTypes) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const { isMobile } = useWindowSize();

  const Icon = DocumentCategoryIcons[icon];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareModalView, setShareModalView] = useState<'add' | 'manage'>('add');
  // const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleMenuClose = (event?: unknown) => {
    if (event && typeof (event as React.MouseEvent).stopPropagation === 'function') {
      (event as React.MouseEvent).stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    action();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClickTag = () => {
    navigate(
      generatePath(Links.resources.documents.tag, {
        tag,
      }),
      {
        state: { tag },
      },
    );
  };

  const { toggleFavourite } = useToggleCategoryFavouriteMutation();

  const onClickFavourite = () => {
    if (categoryId) {
      toggleFavourite({ id: categoryId, isFavourite: !!is_favourite });
    }
  };

  const { deleteCategoryMutation } = useDeleteCategoryMutation();
  const {
    handleOpenModal: openDeleteModal,
    handleCloseModal: closeDeleteModal,
    isOpen: isDeleteModalOpen,
  } = useModal();

  const handleDeleteTarget = async () => {
    if (categoryId) {
      await deleteCategoryMutation({ categoryId });
    }
  };

  const menuContent = [
    <MenuItem
      key="share"
      onClick={handleMenuItemClick(() => {
        setShareModalView('add');
        setIsShareModalOpen(true);
      })}
    >
      <ListItemIcon>
        <ShareIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.menu.share')}</ListItemText>
    </MenuItem>,

    <MenuItem
      key="manage"
      onClick={handleMenuItemClick(() => {
        setShareModalView('manage');
        setIsShareModalOpen(true);
      })}
    >
      <ListItemIcon>
        <LockIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.menu.manage')}</ListItemText>
    </MenuItem>,

    <MenuItem key="edit" onClick={handleMenuItemClick(() => setIsEditModalOpen(true))}>
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.menu.edit')}</ListItemText>
    </MenuItem>,

    <MenuItem key="delete" onClick={handleMenuItemClick(openDeleteModal)}>
      <ListItemIcon>
        <DeleteIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.menu.delete')}</ListItemText>
    </MenuItem>,
  ];

  const menuItems = isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: '16px',
        },
      }}
    >
      <BottomSheetHandle />
      <BottomSheetHeader>
        <IconBox>{Icon}</IconBox>
        <BottomSheetHeaderText>
          <span>{label}</span>
          <span>{description ? description.replace(/<[^>]*>/g, '') : ''}</span>
        </BottomSheetHeaderText>
      </BottomSheetHeader>
      {menuContent}
    </Drawer>
  ) : (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          width: 250,
          borderRadius: '8px',
          boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
          mt: 1,
        },
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {menuContent}
    </Menu>
  );

  const modals = (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {isDeleteModalOpen && (
        <NewConfirmDialog
          title={t('resources.documentCategories.removeModal.title', {
            categoryTitle: label,
          })}
          subTitle={t('resources.documentCategories.removeModal.subtitle')}
          open={isDeleteModalOpen}
          onConfirm={handleDeleteTarget}
          onClose={closeDeleteModal}
        />
      )}
      {isEditModalOpen && (
        <CreateDocumentCategoriesForm
          category={categoryId ? category : undefined}
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          isEdit={true}
        />
      )}
      {isShareModalOpen && (
        <ShareAccessModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          defaultView={shareModalView}
          category={category}
        />
      )}
    </div>
  );

  if (viewMode === 'list') {
    return (
      <CardW
        isManageable={isManageable}
        viewMode="list"
        onClick={isManageable ? onClickTag : undefined}
      >
        <IconBox>{Icon}</IconBox>
        <ListTitle>{label}</ListTitle>
        {!isUntaggedCategory(tag) && (
          <>
            <ListMeta>
              <Description dangerouslySetInnerHTML={{ __html: description || '' }} />
            </ListMeta>
            <ListMeta>
              {t('resources.documentCategories.dateOfCreation')}:{' '}
              {created_at ? formatCreatedAt(created_at) : ''}
            </ListMeta>
            <CardActions>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickFavourite();
                }}
              >
                {is_favourite ? (
                  <Star fontSize="small" htmlColor="#f59e0b" />
                ) : (
                  <StarBorder fontSize="small" />
                )}
              </IconButton>
              {!isSystemCategory(label) && isManager && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(e);
                  }}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
              )}
              {menuItems}
            </CardActions>
          </>
        )}

        {modals}
      </CardW>
    );
  }

  return (
    <CardW isManageable={isManageable} onClick={isManageable ? onClickTag : undefined}>
      <CardLeft>
        <IconBox>{Icon}</IconBox>

        <CardContent>
          <TitleText>{label}</TitleText>
          {!isUntaggedCategory(tag) && (
            <>
              <Description
                dangerouslySetInnerHTML={{
                  __html: description || '',
                }}
              />
              <SubText>
                {t('resources.documentCategories.dateOfCreation')}
                {created_at ? formatCreatedAt(created_at) : ''}
              </SubText>
            </>
          )}
        </CardContent>
      </CardLeft>

      <CardActions>
        {!isUntaggedCategory(tag) && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClickFavourite();
            }}
          >
            {is_favourite ? (
              <Star fontSize="small" htmlColor="#f59e0b" />
            ) : (
              <StarBorder fontSize="small" />
            )}
          </IconButton>
        )}
        {!isSystemCategory(tag) && isManager && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
        {menuItems}
      </CardActions>

      {modals}
    </CardW>
  );
};
